import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { theme, floatingSheet, btnPrimary, inputStyle } from "../theme";
import { api } from "../api/client";
import { CI_SECTORS, CI_SECTOR_PARENTS, subSectors, sectorLabel } from "../data/sectorsData";
import { useNavCountsContext } from "../nav/NavCountsContext";

// Which cyber-security laws apply to an organisation turns on its sector. Until a regulated
// sector is recorded that question is answered by substring-matching a free-text description,
// and it fails silently in both directions: an operator of essential services whose description
// does not happen to contain a keyword is marked exempt, and an ordinary business whose name
// happens to contain one is told it is in scope. Neither is announced.
//
// So this asks whoever can answer it — org admins only; GET /nav/counts returns `sectorPrompt`
// as null for everyone else. "Not now" defers to the end of the browser session; the setup
// checklist and the high-severity action item stay either way, so nothing is lost if it is
// dismissed, and the prompt returns next session until it is answered.

const DEFER_KEY = "abacas.sectorPrompt.deferred";

function deferred(): boolean {
  try { return sessionStorage.getItem(DEFER_KEY) === "1"; } catch { return false; }
}

function defer(): void {
  try { sessionStorage.setItem(DEFER_KEY, "1"); } catch { /* private window — prompt again next load */ }
}

export function SectorPromptModal() {
  const { counts, refresh } = useNavCountsContext();
  const prompt = counts?.role === "client" ? counts.sectorPrompt : null;

  const [dismissed, setDismissed] = useState(deferred);
  const [choice, setChoice] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Pre-select the parent when the prompt is asking to narrow one down.
  useEffect(() => {
    if (prompt?.sectorCode) setChoice(prompt.sectorCode);
  }, [prompt?.sectorCode]);

  if (!prompt || dismissed) return null;

  const ambiguous = prompt.resolution === "ambiguous";
  const chosen = CI_SECTORS.find((s) => s.id === choice) ?? null;
  // Narrowing down means choosing something other than the parent already recorded.
  const canSave = !!choice && (!ambiguous || choice !== prompt.sectorCode);

  async function save() {
    if (!canSave) return;
    setSaving(true); setError("");
    try {
      await api.patch("/organizations/profile", { sectorCode: choice });
      refresh();
      setDismissed(true);
    } catch {
      setError("Could not save. Please try again, or set it on My Organisation.");
    } finally {
      setSaving(false);
    }
  }

  function notNow() {
    defer();
    setDismissed(true);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sector-prompt-title"
      style={{
        position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(0,0,0,0.5)", padding: 16,
      }}
    >
      <div style={{ ...floatingSheet, padding: 20, maxWidth: 520, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid var(--c-line)" }}>
          <AlertTriangle size={20} style={{ color: theme.color.warning, flexShrink: 0, marginTop: 2 }} />
          <div>
            <h2 id="sector-prompt-title" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: theme.color.heading }}>
              {ambiguous ? "Which part of your sector do you operate in?" : "Set your regulated sector"}
            </h2>
            <p style={{ margin: "6px 0 0", fontSize: 13, color: theme.color.subtle, lineHeight: 1.5 }}>
              {ambiguous ? (
                <>
                  You are recorded as <strong>{sectorLabel(prompt.sectorCode) ?? prompt.sectorCode}</strong>, which covers
                  more than one kind of operator. Some cyber-security laws designate only part of it, so they cannot yet
                  tell whether they apply to you.
                </>
              ) : (
                <>
                  Which cyber-security laws apply to you — NIS2, the UK NIS Regulations, the Cyber Security (Jersey) Law
                  and others — turns on your sector. Right now it is only recorded as free text
                  {prompt.sector ? <> (&ldquo;{prompt.sector}&rdquo;)</> : null}, so it is being decided by keyword
                  matching, which can both miss and over-reach without telling you.
                </>
              )}
            </p>
          </div>
        </div>

        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>
          Regulated sector
        </label>
        <select value={choice} onChange={(e) => setChoice(e.target.value)} style={{ ...inputStyle, width: "100%" }}>
          <option value="">— choose —</option>
          {CI_SECTOR_PARENTS.map((parent) => {
            const children = subSectors(parent.id);
            return children.length === 0 ? (
              <option key={parent.id} value={parent.id}>{parent.label}</option>
            ) : (
              <optgroup key={parent.id} label={parent.label}>
                <option value={parent.id}>{parent.label} (all)</option>
                {children.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </optgroup>
            );
          })}
        </select>

        <div style={{ fontSize: 12, color: theme.color.faint, marginTop: 6, minHeight: 32 }}>
          {chosen
            ? chosen.description
            : "If none of these describes you, choose ‘None of these — not a regulated sector’: that is an answer, and a better one than leaving it to a keyword."}
        </div>

        {ambiguous && choice === prompt.sectorCode && (
          <div style={{ fontSize: 12, color: theme.color.warning, marginTop: 4 }}>
            Choose a more specific option than the one already recorded.
          </div>
        )}
        {error && <div style={{ fontSize: 12, color: theme.color.danger, marginTop: 8 }}>{error}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button
            onClick={notNow}
            style={{
              background: "transparent", border: `1px solid ${theme.color.border}`, color: theme.color.subtle,
              borderRadius: 3, padding: "8px 14px", fontSize: 13, cursor: "pointer",
            }}
          >
            Not now
          </button>
          <button onClick={save} disabled={!canSave || saving} style={{ ...btnPrimary, opacity: !canSave || saving ? 0.5 : 1 }}>
            {saving ? "Saving…" : "Save sector"}
          </button>
        </div>
      </div>
    </div>
  );
}
